import { useState } from "react";
import { base44 } from "@/api/base44Client";

const STATUS_STEPS_AR = [
  { key: "pending",    label: "تم الاستلام",      icon: "📥", desc: "طلبك في الانتظار" },
  { key: "assigned",   label: "تعيين المندوب",    icon: "👤", desc: "تم تعيين مندوب" },
  { key: "picked_up",  label: "استلام الملابس",   icon: "🚗", desc: "المندوب في طريقه" },
  { key: "sorting",    label: "الفرز والتقييم",   icon: "🔍", desc: "يتم فرز الملابس" },
  { key: "processing", label: "التنظيف",          icon: "🫧", desc: "جاري التنظيف" },
  { key: "ready",      label: "جاهز للتسليم",     icon: "✅", desc: "ملابسك جاهزة" },
  { key: "delivering", label: "في التوصيل",       icon: "🚚", desc: "المندوب في الطريق" },
  { key: "delivered",  label: "تم التسليم",       icon: "🎉", desc: "اكتمل الطلب" },
];

const STATUS_STEPS_EN = [
  { key: "pending",    label: "Received",          icon: "📥", desc: "Your order is pending" },
  { key: "assigned",   label: "Delegate Assigned", icon: "👤", desc: "A delegate has been assigned" },
  { key: "picked_up",  label: "Clothes Picked Up", icon: "🚗", desc: "Delegate is on the way" },
  { key: "sorting",    label: "Sorting",           icon: "🔍", desc: "Clothes are being sorted" },
  { key: "processing", label: "Cleaning",          icon: "🫧", desc: "Cleaning in progress" },
  { key: "ready",      label: "Ready for Delivery",icon: "✅", desc: "Your clothes are ready" },
  { key: "delivering", label: "Out for Delivery",  icon: "🚚", desc: "Delegate is on the way" },
  { key: "delivered",  label: "Delivered",         icon: "🎉", desc: "Order completed" },
];

const statusOrder = STATUS_STEPS_AR.map(s => s.key);

export default function LaundryOrderTrack({ lang = "ar" }) {
  const isAr = lang === "ar";
  const STATUS_STEPS = isAr ? STATUS_STEPS_AR : STATUS_STEPS_EN;

  const [searchVal, setSearchVal] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchVal.trim()) return;
    setLoading(true);
    setNotFound(false);
    setOrders([]);
    setSelectedOrder(null);

    const val = searchVal.trim();
    // Try order number first
    let found = await base44.entities.LaundryOrder.filter({ order_number: val.toUpperCase() });
    if (found.length === 0) {
      // Search by phone — show all non-delivered/non-cancelled orders
      found = await base44.entities.LaundryOrder.filter({ customer_phone: val });
      // Filter to only active (not completed) orders
      const active = found.filter(o => o.status !== "delivered" && o.status !== "cancelled");
      found = active.length > 0 ? active : found; // fallback to all if all completed
    }

    if (found.length === 0) {
      setNotFound(true);
    } else {
      const sorted = found.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
      setOrders(sorted);
      setSelectedOrder(sorted[0]);
    }
    setLoading(false);
  };

  const order = selectedOrder;
  const currentStep = order ? statusOrder.indexOf(order.status) : -1;

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h2 className="text-xl font-black text-gray-900 dark:text-white">{isAr ? "تتبع طلبك" : "Track Your Order"}</h2>
        <p className="text-sm text-gray-500 mt-1">{isAr ? "أدخل رقم طلبك أو رقم جوالك" : "Enter your order number or mobile number"}</p>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          value={searchVal}
          onChange={e => setSearchVal(e.target.value)}
          className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-gray-900 dark:text-white"
          placeholder={isAr ? "رقم الطلب أو رقم الجوال" : "Order number or mobile number"}
          dir="ltr"
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-cyan-600 text-white px-5 py-3 rounded-2xl font-bold hover:bg-cyan-700 transition-all disabled:opacity-60"
        >
          {loading ? "⏳" : "🔍"}
        </button>
      </form>

      {/* Multiple orders selector */}
      {orders.length > 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-3">
            {isAr ? `وجدنا ${orders.length} طلبات نشطة — اختر الطلب:` : `Found ${orders.length} active orders — select one:`}
          </p>
          <div className="space-y-2">
            {orders.map(o => (
              <button
                key={o.id}
                onClick={() => setSelectedOrder(o)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-all border-2 ${
                  selectedOrder?.id === o.id
                    ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20"
                    : "border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <span className="font-black text-cyan-600 tracking-wider">{o.order_number}</span>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                  o.status === "delivered" ? "bg-green-100 text-green-700" :
                  o.status === "cancelled" ? "bg-red-100 text-red-700" :
                  "bg-cyan-100 text-cyan-700"
                }`}>
                  {STATUS_STEPS.find(s => s.key === o.status)?.label || o.status}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {notFound && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-4 text-center text-red-600 dark:text-red-400 text-sm">
          {isAr ? "لم يتم العثور على طلب بهذا الرقم" : "No order found with this number"}
        </div>
      )}

      {order && (
        <div className="space-y-4">
          {/* Order summary */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-gray-500">{isAr ? "رقم الطلب" : "Order Number"}</p>
                <p className="font-black text-cyan-600 text-lg tracking-wider">{order.order_number}</p>
              </div>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                order.status === "delivered" ? "bg-green-100 text-green-700" :
                order.status === "cancelled" ? "bg-red-100 text-red-700" :
                "bg-cyan-100 text-cyan-700"
              }`}>
                {STATUS_STEPS.find(s => s.key === order.status)?.label || order.status}
              </span>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <p>👤 {order.customer_name}</p>
              {order.delegate_name && <p>🚗 {isAr ? "المندوب:" : "Delegate:"} {order.delegate_name}</p>}
              {order.total_amount > 0 && (
                <p className="font-bold text-gray-900 dark:text-white">
                  💰 {isAr ? "إجمالي الفاتورة:" : "Invoice Total:"} {order.total_amount} {isAr ? "ر.س" : "SAR"}
                  {order.paid && <span className="text-green-600 mr-2">✅ {isAr ? "مدفوع" : "Paid"}</span>}
                </p>
              )}
            </div>
          </div>

          {/* Progress steps */}
          {order.status !== "cancelled" && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="font-bold text-gray-700 dark:text-gray-300 mb-4 text-sm">{isAr ? "مراحل الطلب" : "Order Stages"}</h3>
              <div className="space-y-3">
                {STATUS_STEPS.map((step, idx) => {
                  const isDone = idx <= currentStep;
                  const isCurrent = idx === currentStep;
                  return (
                    <div key={step.key} className={`flex items-center gap-3 p-2 rounded-xl transition-all ${isCurrent ? "bg-cyan-50 dark:bg-cyan-900/20" : ""}`}>
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-lg flex-shrink-0 ${
                        isDone ? "bg-cyan-100 dark:bg-cyan-900/40" : "bg-gray-100 dark:bg-gray-700 opacity-40"
                      }`}>
                        {step.icon}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-bold ${isDone ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-600"}`}>
                          {step.label}
                        </p>
                        {isCurrent && <p className="text-xs text-cyan-600 dark:text-cyan-400">{step.desc}</p>}
                      </div>
                      {isDone && !isCurrent && <span className="text-green-500 text-sm">✓</span>}
                      {isCurrent && <span className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Invoice — shown only after admin marks invoice_sent */}
          {order.invoice_sent && order.services && order.services.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-amber-200 dark:border-amber-800">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-700 dark:text-gray-300 text-sm">📋 {isAr ? "فاتورتك" : "Your Invoice"}</h3>
                {order.paid
                  ? <span className="text-xs font-black bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 px-3 py-1 rounded-full">✅ {isAr ? "مدفوعة" : "Paid"}</span>
                  : <span className="text-xs font-black bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-3 py-1 rounded-full">⏳ {isAr ? "في انتظار الدفع" : "Pending Payment"}</span>
                }
              </div>
              <div className="space-y-2">
                {order.services.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{s.service_name} × {s.quantity}</span>
                    <span className="font-bold text-gray-900 dark:text-white">{s.total} {isAr ? "ر.س" : "SAR"}</span>
                  </div>
                ))}
                {order.discount > 0 && (
                  <div className="flex items-center justify-between text-sm text-green-600 dark:text-green-400">
                    <span>{isAr ? "خصم" : "Discount"}</span>
                    <span>- {order.discount} {isAr ? "ر.س" : "SAR"}</span>
                  </div>
                )}
                <div className="border-t border-gray-100 dark:border-gray-700 pt-2 flex justify-between font-black text-base">
                  <span>{isAr ? "الإجمالي" : "Total"}</span>
                  <span className="text-cyan-600">{order.total_amount} {isAr ? "ر.س" : "SAR"}</span>
                </div>
              </div>
            </div>
          )}

          {/* Pending invoice notice */}
          {!order.invoice_sent && order.status !== "cancelled" && order.status !== "delivered" && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-4 text-center border border-amber-100 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">
                {isAr ? "⏳ سيتم إرسال فاتورتك بعد فرز الملابس وتحديد الخدمات" : "⏳ Your invoice will be sent after sorting and service assessment"}
              </p>
            </div>
          )}

          {/* Rating CTA — shown when invoice is sent */}
          {order.invoice_sent && (
            <a
              href={`/rate?orderId=${order.id}&orderNumber=${encodeURIComponent(order.order_number || "")}&name=${encodeURIComponent(order.customer_name || "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-2xl p-5 text-center shadow-lg hover:opacity-90 transition-all"
            >
              <div className="text-3xl mb-1">⭐</div>
              <p className="font-black text-base">{isAr ? "قيّم تجربتك معنا" : "Rate Your Experience"}</p>
              <p className="text-sm text-white/80 mt-0.5">{isAr ? "رأيك يساعدنا على التطوير" : "Your feedback helps us improve"}</p>
            </a>
          )}
        </div>
      )}

      {!order && !notFound && !loading && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-5xl mb-3">🔍</div>
          <p className="text-sm">{isAr ? "أدخل رقم الطلب أو رقم الجوال للبحث" : "Enter your order or mobile number to search"}</p>
        </div>
      )}
    </div>
  );
}